import { useCallback, useRef } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  tags: string[];
  accessCount: number;
}

interface CacheOptions {
  ttl?: number;
  tags?: string[];
}

interface CacheMetrics {
  size: number;
  maxSize: number;
  hitRate: number;
  totalRequests: number;
  totalHits: number;
}

/**
 * Unified caching solution to replace multiple competing cache implementations
 * Features: TTL expiration, LRU eviction, tagging, metrics, request deduplication
 */
export const useUnifiedCache = <T = any>() => {
  const cacheRef = useRef<Map<string, CacheEntry<T>>>(new Map());
  const pendingRequestsRef = useRef<Map<string, Promise<T>>>(new Map());
  const metricsRef = useRef<CacheMetrics>({
    size: 0,
    maxSize: 1000,
    hitRate: 0,
    totalRequests: 0,
    totalHits: 0
  });

  // Update metrics
  const updateMetrics = useCallback((hit: boolean) => {
    const metrics = metricsRef.current;
    metrics.totalRequests++;
    if (hit) metrics.totalHits++;
    metrics.hitRate = (metrics.totalHits / metrics.totalRequests) * 100;
    metrics.size = cacheRef.current.size;
  }, []);

  // LRU eviction when cache is full
  const evictLRU = useCallback(() => {
    const entries = Array.from(cacheRef.current.entries());
    const sortedByAccess = entries.sort((a, b) => a[1].accessCount - b[1].accessCount);
    
    // Remove 20% of least accessed items
    const toRemove = sortedByAccess.slice(0, Math.floor(entries.length * 0.2));
    toRemove.forEach(([key]) => {
      cacheRef.current.delete(key);
    });
  }, []);

  // Generate cache key with tags
  const getCacheKey = useCallback((baseKey: string, tags?: string[]) => {
    return tags && tags.length > 0 ? `${baseKey}:${tags.sort().join(',')}` : baseKey;
  }, []);

  // Set cache entry
  const set = useCallback((
    key: string,
    data: T,
    options: CacheOptions = {}
  ) => {
    const { ttl = 5 * 60 * 1000, tags = [] } = options;
    
    // Evict if cache is full
    if (cacheRef.current.size >= metricsRef.current.maxSize) {
      evictLRU();
    }

    const cacheKey = getCacheKey(key, tags);
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      tags,
      accessCount: 0
    };

    cacheRef.current.set(cacheKey, entry);
  }, [getCacheKey, evictLRU]);

  // Get cache entry
  const get = useCallback((key: string, tags?: string[]): T | null => {
    const cacheKey = getCacheKey(key, tags);
    const entry = cacheRef.current.get(cacheKey);

    updateMetrics(!!entry);

    if (!entry) return null;

    // Check TTL expiration
    if (Date.now() - entry.timestamp > entry.ttl) {
      cacheRef.current.delete(cacheKey);
      return null;
    }

    // Update access count for LRU
    entry.accessCount++;

    return entry.data;
  }, [getCacheKey, updateMetrics]);

  // Execute with cache and request deduplication
  const executeWithCache = useCallback(async (
    key: string,
    executor: () => Promise<T>,
    options: CacheOptions = {}
  ): Promise<T> => {
    const cacheKey = getCacheKey(key, options.tags);
    
    // Check cache first
    const cached = get(key, options.tags);
    if (cached !== null) {
      return cached;
    }

    // Check for pending request (deduplication)
    if (pendingRequestsRef.current.has(cacheKey)) {
      return pendingRequestsRef.current.get(cacheKey)!;
    }

    // Create new request
    const request = executor().finally(() => {
      pendingRequestsRef.current.delete(cacheKey);
    });

    pendingRequestsRef.current.set(cacheKey, request);

    try {
      const result = await request;
      set(key, result, options);
      return result;
    } catch (error) {
      // Don't cache errors
      throw error;
    }
  }, [getCacheKey, get, set]);

  // Invalidate by tags
  const invalidateByTags = useCallback((tags: string[]) => {
    const keysToDelete: string[] = [];
    
    cacheRef.current.forEach((entry, key) => {
      const hasMatchingTag = tags.some(tag => entry.tags.includes(tag));
      if (hasMatchingTag) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => {
      cacheRef.current.delete(key);
      pendingRequestsRef.current.delete(key);
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(`[UnifiedCache] Invalidated ${keysToDelete.length} entries with tags:`, tags);
    }
  }, []);

  // Invalidate specific key
  const invalidate = useCallback((key: string, tags?: string[]) => {
    const cacheKey = getCacheKey(key, tags);
    cacheRef.current.delete(cacheKey);
    pendingRequestsRef.current.delete(cacheKey);
  }, [getCacheKey]);

  // Clear all cache
  const clear = useCallback(() => {
    cacheRef.current.clear();
    pendingRequestsRef.current.clear();
    metricsRef.current = {
      size: 0,
      maxSize: 1000,
      hitRate: 0,
      totalRequests: 0,
      totalHits: 0
    };
  }, []);

  // Get cache statistics
  const getMetrics = useCallback(() => {
    return { ...metricsRef.current };
  }, []);

  return {
    set,
    get,
    executeWithCache,
    invalidate,
    invalidateByTags,
    clear,
    getMetrics
  };
};